import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { UPDATE_USER } from '../graphql/user/mutations';
import { useContextStore } from '../hooks/context/useContextStore';
import { isImpersonating } from '../utils/impersonationSession';
import Spinner from './ui/Spinner';

export default function AnalyticsConsentPrompt() {
  const { pathname } = useLocation();
  const { clientUser, updateUserProfile } = useContextStore();
  const [updateConsent, { loading }] = useMutation(UPDATE_USER);
  const [pendingConsent, setPendingConsent] = useState<boolean | null>(null);

  if (
    isImpersonating() ||
    pathname === '/set-password' ||
    !clientUser ||
    (clientUser.analyticsConsent !== null && clientUser.analyticsConsent !== undefined)
  )
    return null;

  const isPending = loading || pendingConsent !== null;

  const decide = async (consent: boolean) => {
    setPendingConsent(consent);
    try {
      const { data } = await updateConsent({ variables: { data: { analyticsConsent: consent } } });
      const user = data?.updateUser?.user;
      if (!user) return;
      updateUserProfile({
        analyticsConsent: user.analyticsConsent,
        analyticsConsentAt: user.analyticsConsentAt,
        analyticsConsentVersion: user.analyticsConsentVersion,
      });
    } finally {
      setPendingConsent(null);
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="analytics-consent-title"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 id="analytics-consent-title" className="text-xl font-semibold">
          Help improve Direct Home Service
        </h2>
        <p className="mt-3 text-sm text-ds-text-secondary">
          May we use optional Mixpanel analytics? This is optional and can be changed later in
          Privacy Settings.
        </p>
        <a
          href="https://directhomeservice.com/privacy-analytics/"
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block text-sm text-primary underline cursor-pointer"
        >
          Read the Analytics Privacy Notice
        </a>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={isPending}
            onClick={() => void decide(false)}
            className="flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors cursor-pointer hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pendingConsent === false && <Spinner size="small" color="currentColor" />}
            No thanks
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() => void decide(true)}
            className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors cursor-pointer hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pendingConsent === true && <Spinner size="small" color="white" />}
            Allow analytics
          </button>
        </div>
      </div>
    </div>
  );
}
